import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from './firebase';

// Mirror the cap enforced in firebase/storage.rules (/cgc/**).
export const MAX_DOC_BYTES = 25 * 1024 * 1024; // 25 MB

/**
 * Stable, non-reversible hash of an auth uid, used as the uploader segment of
 * the CGC storage path (`/cgc/{clubId}/{uploaderHash}/...`). Encodes WHO
 * uploaded the document for attribution / abuse-tracing without putting the
 * raw uid in the (publicly-readable) path. SHA-256 → first 16 hex chars
 * (64 bits — ample to attribute, short enough for a tidy path).
 */
export async function uploaderHash(uid) {
  if (!uid) throw new Error('uploaderHash: uid is required');
  const data = new TextEncoder().encode(`racemarks-cgc:${uid}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

/** Storage-path-safe document id segment. */
function safeDocId(docId) {
  return String(docId || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64) || `doc-${Date.now()}`;
}

/**
 * Build the CGC (Club Generated Content) object path for a club document PDF:
 *   cgc/{clubId}/{uploaderHash(uid)}/{docId}.pdf
 */
export async function clubDocPath({ clubId, uid, docId }) {
  const hash = await uploaderHash(uid);
  return `cgc/${clubId}/${hash}/${safeDocId(docId)}.pdf`;
}

/**
 * Upload a committee document PDF and return its download URL — to store in the
 * document's `url` field, exactly like a pasted public link (the app consumes
 * it unchanged via content_sync_service.refFromURL).
 *
 * Validates type + size client-side for a fast/clear error; storage.rules
 * enforce the same server-side (the rules, not the client, are the gate).
 * Re-uploading the same docId overwrites in place (replace-on-edit; full
 * version history is a follow-up).
 */
export async function uploadClubDocPdf({ clubId, uid, docId, file }) {
  if (!clubId || !uid) {
    throw new Error('uploadClubDocPdf: clubId and uid are required');
  }
  if (!file) throw new Error('uploadClubDocPdf: no file provided');
  if (file.type !== 'application/pdf') {
    throw new Error('Only PDF files can be uploaded.');
  }
  if (file.size >= MAX_DOC_BYTES) {
    throw new Error(
      `PDF too large — must be under ${Math.floor(MAX_DOC_BYTES / 1024 / 1024)} MB.`,
    );
  }
  // Defence-in-depth: verify the real file header is a PDF (`%PDF-`), not just
  // the client-declared MIME type. This (like the storage.rules contentType
  // check) is still client-side and bypassable by a custom client — it catches
  // accidental / broken uploads (e.g. a .docx mislabelled). True byte-level
  // validation for a determined admin-credentialed attacker needs a server-side
  // Cloud Function (follow-up — see firebase/storage.rules).
  const header = new Uint8Array(await file.slice(0, 5).arrayBuffer());
  const isPdfMagic =
    header[0] === 0x25 && // %
    header[1] === 0x50 && // P
    header[2] === 0x44 && // D
    header[3] === 0x46 && // F
    header[4] === 0x2d; // -
  if (!isPdfMagic) {
    throw new Error('That file is not a valid PDF (missing %PDF- header).');
  }
  const path = await clubDocPath({ clubId, uid, docId });
  const objectRef = ref(storage, path);
  const hash = path.split('/')[2];
  await uploadBytes(objectRef, file, {
    contentType: 'application/pdf',
    customMetadata: { uploaderHash: hash, clubId, docId: safeDocId(docId) },
  });
  return getDownloadURL(objectRef);
}

// NOTE: orphan cleanup (deleting the Storage blob when a doc record is removed)
// is a deliberate follow-up. It can't key on uploaderHash(currentUser) — a
// different admin may delete the doc, producing the wrong hash/path. Reliable
// cleanup needs the stored object path on the doc record, or a server-side
// sweep. Until then, re-uploading the same docId overwrites in place.
